// Extraction mécanique de client.ts (WO-V3-02 / US-019) — méthodes API du domaine.
import { request, ApiError, httpMessage, BASE } from './http'
import { getToken } from '../auth/session'
import type { AiModelCatalog, AiProviderConnection, GlobalAiActivation, GlobalAiConnection, GlobalAiModel, GlobalOrganisation, McpServerPackage, NotificationChannel, OperationsHealth } from './types/aiAdmin'
import { approvalDecisionBody, specGateRenewalBody } from './types/approvals'
import type { AiKillSwitchHistoryRow, AiKillSwitchState } from './types/aiAdmin'
import type { ApprovalDetail, ApprovalLaunchedJob, ApprovalSummary, JobSummary, ReviewVerdict, PortfolioRow, RecentDecision, SemanticHistoryHit, SpecGateDecisionEvidence, SpecGateEvaluation, SpecGateEvidenceDetail, SpecGateRenewalResult } from './types/approvals'
import type { ChatMessage, ChatReply, Dependency, EtatConformite, Feedback, ListeWorkers, MatriceRoles, Permission, PolitiqueAcces, SimulationAcces, SimulationRetention, WorkspaceQuota } from './types/collaboration'

export const adminModelsQuotasAndCollabApi = {
  // ---- Quotas : écriture (Lot 3) ----------------------------------------
  //
  // `PUT` fait l'upsert, rendu possible par la clé métier posée en V57. Les trois verbes
  // expriment trois intentions distinctes : remettre un compteur à zéro n'est PAS supprimer
  // la limite, et un administrateur qui veut débloquer un espace saturé sans renoncer à son
  // plafond n'avait, sans `reset`, que la suppression.
  enregistrerQuota: (workspaceId: string, resourceType: string, limite: number) =>
    request<WorkspaceQuota>('/admin/quotas', {
      method: 'PUT',
      body: JSON.stringify({
        workspace_id: workspaceId, resource_type: resourceType, limit_value: limite,
      }),
    }),
  supprimerQuota: (workspaceId: string, resourceType: string) =>
    request<{ deleted: boolean }>(
      `/admin/quotas?workspace_id=${encodeURIComponent(workspaceId)}`
      + `&resource_type=${encodeURIComponent(resourceType)}`, { method: 'DELETE' }),
  reinitialiserQuota: (workspaceId: string, resourceType: string) =>
    request<WorkspaceQuota>(
      `/admin/quotas/reset?workspace_id=${encodeURIComponent(workspaceId)}`
      + `&resource_type=${encodeURIComponent(resourceType)}`, { method: 'POST' }),

  // ---- Modèles autorisés : liste BLANCHE (Lot 3) ------------------------
  //
  // `allowed_count` est renvoyé par la passerelle parce qu'il change la nature de la liste :
  // à zéro, aucun modèle ne passe ; dès un, seuls ceux-là passent.
  autoriserModele: (workspaceId: string, modelId: string) =>
    request<{ model_id: string; allowed_count: number }>('/admin/allowed-models', {
      method: 'POST',
      body: JSON.stringify({ workspace_id: workspaceId, model_id: modelId }),
    }),
  retirerModele: (workspaceId: string, modelId: string) =>
    request<{ model_id: string; allowed_count: number }>(
      `/admin/allowed-models/${encodeURIComponent(modelId)}`
      + `?workspace_id=${encodeURIComponent(workspaceId)}`, { method: 'DELETE' }),

  /** FND-U6B-01 : état composé de l'arrêt d'urgence, lisible par tout membre (lecteur compris). */
  getAiKillSwitchState: () => request<AiKillSwitchState>('/ai/kill-switch/state'),
  listAiConnections: () => request<AiProviderConnection[]>('/admin/ai/connections'),
  getAiCatalog: () => request<AiModelCatalog>('/admin/ai/catalog'),
  grantAiModel: (modelId: string, enabled = true) =>
    request<{ organisation_id: string; model_id: string; enabled: boolean }>('/admin/ai/grants', {
      method: 'POST', body: JSON.stringify({ model_id: modelId, enabled }),
    }),
  revokeAiModel: (modelId: string) =>
    request<{ organisation_id: string; model_id: string; revoked: boolean }>(
      `/admin/ai/grants/${encodeURIComponent(modelId)}`, { method: 'DELETE' }),
  listMcpServers: () => request<McpServerPackage[]>('/admin/mcp/servers'),
  importMcpArchive: (file: File, sourceType: 'upload' | 'gitlab' | 'url' = 'upload', sourceUri?: string) => {
    const body = new FormData()
    body.append('file', file)
    body.append('source_type', sourceType)
    if (sourceUri?.trim()) body.append('source_uri', sourceUri.trim())
    return request<McpServerPackage>('/admin/mcp/servers', { method: 'POST', body })
  },
  importMcpSource: (sourceType: 'gitlab' | 'url', sourceUri: string, sourceRevision?: string) =>
    request<McpServerPackage>('/admin/mcp/servers/from-source', {
      method: 'POST',
      body: JSON.stringify({
        source_type: sourceType,
        source_uri: sourceUri.trim(),
        ...(sourceRevision?.trim() ? { source_revision: sourceRevision.trim() } : {}),
      }),
    }),
  approveMcpServer: (serverId: string) =>
    request<{ server_id: string; status: string }>(`/admin/mcp/servers/${encodeURIComponent(serverId)}/approve`, { method: 'POST' }),
  setMcpServerEnabled: (serverId: string, enabled: boolean) =>
    request<{ server_id: string; enabled: boolean }>(`/admin/mcp/servers/${encodeURIComponent(serverId)}/activation`, {
      method: 'POST', body: JSON.stringify({ enabled }),
    }),
  listMcpServerAgents: (serverId: string) =>
    request<Array<{ agent_id: string; name: string; enabled: boolean }>>(
      `/admin/mcp/servers/${encodeURIComponent(serverId)}/agents`),
  setMcpServerAgent: (serverId: string, agentId: string, enabled: boolean) =>
    request<{ server_id: string; agent_id: string; enabled: boolean }>(
      `/admin/mcp/servers/${encodeURIComponent(serverId)}/agents/${encodeURIComponent(agentId)}`, {
        method: 'PUT', body: JSON.stringify({ enabled }),
      }),
  createAiConnection: (body: {
    provider: string; display_name: string; endpoint: string; secret_ref?: string; enabled?: boolean
  }) => request<AiProviderConnection>('/admin/ai/connections', {
    method: 'POST', body: JSON.stringify(body),
  }),
  setAiConnectionEnabled: (id: string, enabled: boolean) =>
    request<{ connection_id: string; enabled: boolean }>(
      `/admin/ai/connections/${encodeURIComponent(id)}`, {
        method: 'PATCH', body: JSON.stringify({ enabled }),
      }),
  listGlobalAiConnections: () =>
    request<GlobalAiConnection[]>('/admin/ai/platform/connections'),
  listGlobalOrganisations: () =>
    request<GlobalOrganisation[]>('/admin/ai/platform/organisations'),
  listGlobalAiModels: () =>
    request<GlobalAiModel[]>('/admin/ai/platform/models'),
  createGlobalAiModel: (body: {
    model_id: string; name: string; type: 'chat' | 'code' | 'embeddings'; provider: string
    connection_id?: string; capabilities?: string; context_length?: number
  }) => request<{ model_id: string; name: string; type: string; provider: string; connection_id?: string | null; created: boolean }>(
    '/admin/ai/platform/models', { method: 'POST', body: JSON.stringify(body) }),
  getGlobalAiKillSwitch: () =>
    request<{ enabled: boolean; reason?: string; updated_at?: string | null; updated_by?: string | null }>('/admin/ai/platform/kill-switch'),
  // DEC-052 : la passerelle exige `confirmation` = « plateforme » (sinon 400
  // AI_KILL_SWITCH_CONFIRMATION_REQUIRED, rien ne change).
  setGlobalAiKillSwitch: (body: { enabled: boolean; reason: string; confirmation: string }) =>
    request<{ scope: string; enabled: boolean }>('/admin/ai/platform/kill-switch', {
      method: 'PUT', body: JSON.stringify(body),
    }),
  // U6b vague 1 (§ 4.10.1, DIA3-24) : l'arrêt d'urgence à la portée de l'ORGANISATION et
  // l'historique commun append-only. La passerelle exige `confirmation` = nom de l'organisation.
  getOrgAiKillSwitch: () =>
    request<{ organisation_id?: string; enabled: boolean; reason?: string; updated_at?: string | null }>('/admin/ai/kill-switch'),
  setOrgAiKillSwitch: (body: { enabled: boolean; reason: string; confirmation: string }) =>
    request<{ scope: string; enabled: boolean }>('/admin/ai/kill-switch', {
      method: 'PUT', body: JSON.stringify(body),
    }),
  getOrgAiKillSwitchHistory: (limit = 50) =>
    request<AiKillSwitchHistoryRow[]>(`/admin/ai/kill-switch/history?limit=${limit}`),
  getGlobalAiKillSwitchHistory: (limit = 100) =>
    request<AiKillSwitchHistoryRow[]>(`/admin/ai/platform/kill-switch/history?limit=${limit}`),
  bindGlobalAiModelConnection: (modelId: string, connectionId: string | null) =>
    request<{ model_id: string; connection_id: string }>(
      `/admin/ai/platform/models/${encodeURIComponent(modelId)}/connection`, {
        method: 'PUT', body: JSON.stringify({ connection_id: connectionId }),
      }),
  createGlobalAiConnection: (body: {
    provider: string; display_name: string; endpoint: string; secret_ref?: string; enabled?: boolean
  }) => request<GlobalAiConnection>('/admin/ai/platform/connections', {
    method: 'POST', body: JSON.stringify(body),
  }),
  setGlobalAiConnectionEnabled: (connectionId: string, enabled: boolean) =>
    request<{ connection_id: string; enabled: boolean }>(
      `/admin/ai/platform/connections/${encodeURIComponent(connectionId)}`,
      { method: 'PATCH', body: JSON.stringify({ enabled }) }),
  setGlobalAiConnectionOrganisation: (connectionId: string, organisationId: string, enabled: boolean) =>
    request<{ connection_id: string; organisation_id: string; enabled: boolean }>(
      `/admin/ai/platform/connections/${encodeURIComponent(connectionId)}/organisations/${encodeURIComponent(organisationId)}/activation`,
      { method: 'POST', body: JSON.stringify({ enabled }) }),
  listGlobalAiConnectionOrganisations: (connectionId: string) =>
    request<GlobalAiActivation[]>(`/admin/ai/platform/connections/${encodeURIComponent(connectionId)}/organisations`),
  probeGlobalAiConnection: (connectionId: string) =>
    request<{ connection_id: string; status: string; http_status?: number | null; checked_endpoint: string }>(
      `/admin/ai/platform/connections/${encodeURIComponent(connectionId)}/health`, { method: 'POST' }),
  discoverGlobalAiModels: (connectionId: string) =>
    request<{ connection_id: string; provider: string; discovered: number; created_or_updated: number; skipped: number }>(
      `/admin/ai/platform/connections/${encodeURIComponent(connectionId)}/discover-models`, { method: 'POST' }),
  probeLangfuse: () =>
    request<{ status: string; http_status?: number; trace_id: string; checked_at: string; probe_latency_ms?: number; index_status?: 'confirmed' | 'pending' | 'not_configured'; index_latency_ms?: number; index_observed_at?: string; reason?: string }>(
      '/admin/observability/langfuse/probe', { method: 'POST' }),
  probeLangfuseIndexation: (traceId: string) =>
    request<{ trace_id: string; checked_at: string; index_status?: 'confirmed' | 'pending' | 'not_configured'; index_latency_ms?: number; index_observed_at?: string }>(
      `/admin/observability/langfuse/probe/${encodeURIComponent(traceId)}`),
  getOperationsHealth: () => request<OperationsHealth>('/admin/operations/health'),
  listNotificationChannels: () =>
    request<NotificationChannel[]>('/admin/notification-channels'),
  upsertNotificationChannel: (body: {
    channel_id?: string; provider: NotificationChannel['provider']; display_name: string
    endpoint: string; secret_ref?: string; enabled?: boolean
  }) => request<{ channel_id: string; provider: string; enabled: boolean }>(
    '/admin/notification-channels', { method: 'PUT', body: JSON.stringify(body) }),

  // ---- Habilitations : matrice, politiques, simulateur (Lot 3) ----------
  listerPermissions: () => request<Permission[]>('/admin/permissions'),
  matriceRoles: () => request<MatriceRoles>('/admin/role-permissions'),
  accorderPermission: (role: string, permission: string) =>
    request<{ granted: boolean }>(
      `/admin/role-permissions/${encodeURIComponent(role)}/${encodeURIComponent(permission)}`,
      { method: 'POST' }),
  retirerPermission: (role: string, permission: string) =>
    request<{ granted: boolean }>(
      `/admin/role-permissions/${encodeURIComponent(role)}/${encodeURIComponent(permission)}`,
      { method: 'DELETE' }),

  listerPolitiques: () => request<PolitiqueAcces[]>('/admin/access-policies'),
  creerPolitique: (body: {
    role?: string | null; permission: string; effect: 'allow' | 'deny'
    condition: Record<string, string>; description?: string
  }) => request<{ id: number }>('/admin/access-policies',
    { method: 'POST', body: JSON.stringify(body) }),
  supprimerPolitique: (id: number) =>
    request<{ deleted: boolean }>(`/admin/access-policies/${id}`, { method: 'DELETE' }),

  /** Simule la décision RÉELLE : le serveur appelle `AccessDecision`, il ne la rejoue pas. */
  simulerDecision: (body: {
    roles: string[]; permission: string; attributes: Record<string, string>
  }) => request<SimulationAcces>('/admin/access-decision/simulate',
    { method: 'POST', body: JSON.stringify(body) }),

  // ---- Workers : périmètre GLOBAL (table sans locataire) ----------------
  listerWorkers: () => request<ListeWorkers>('/admin/workers'),
  purgerWorker: (workerId: string) =>
    request<{ purged: boolean }>(`/admin/workers/${encodeURIComponent(workerId)}`,
      { method: 'DELETE' }),

  // ---- Conformité : pseudonymes, rétention, purge ----------------------
  etatConformite: (workspaceId: string) =>
    request<EtatConformite>(
      `/admin/conformite?workspace_id=${encodeURIComponent(workspaceId)}`),
  simulerRetention: (workspaceId: string, jours: number) =>
    request<SimulationRetention>(
      `/admin/retention/simulation?workspace_id=${encodeURIComponent(workspaceId)}&days=${jours}`),
  purgerRetention: (workspaceId: string, jours: number, attendu: number) =>
    request<{ events_deleted: number }>(
      `/admin/retention/purge?workspace_id=${encodeURIComponent(workspaceId)}`
      + `&days=${jours}&expected=${attendu}`, { method: 'POST' }),

  /** Retours humains de l'espace (note 1-5 + commentaire), du plus récent au plus ancien. */
  listFeedback: (workspaceId: string) =>
    request<Feedback[]>(`/feedback?workspace_id=${encodeURIComponent(workspaceId)}`),

  submitFeedback: (workspaceId: string, rating: number, comment?: string, jobId?: string) =>
    request<{ id: number; rating: number }>('/feedback', {
      method: 'POST',
      body: JSON.stringify({
        workspace_id: workspaceId,
        rating,
        ...(comment ? { comment } : {}),
        ...(jobId ? { job_id: jobId } : {}),
      }),
    }),

  /** Portefeuille du locataire (REQ-F-0041) : portée du jeton, aucun paramètre. */
  portfolio: () => request<PortfolioRow[]>('/portfolio'),

  /** Décisions récentes du locataire (REQ-NF-0014). */
  recentDecisions: () => request<RecentDecision[]>('/portfolio/decisions'),

  /** Dépose une idée brute : elle devient un document et un job de cadrage (REQ-F-0037). */
  submitIdea: (workspaceId: string, ideaText: string) =>
    request<{ document_id: string; filename: string; status: string; message: string }>(
      '/ideas',
      { method: 'POST',
        body: JSON.stringify({ workspace_id: workspaceId, idea_text: ideaText }) }),

  listApprovals: (workspaceId: string, status?: 'pending' | 'approved' | 'rejected',
                  view?: 'mine' | 'others' | 'done') =>
    request<ApprovalSummary[]>(
      `/approvals?workspace_id=${encodeURIComponent(workspaceId)}${status ? `&status=${status}` : ''}${view ? `&view=${view}` : ''}`),

  getApproval: (id: number) => request<ApprovalDetail>(`/approvals/${id}`),

  /** Lance ou réutilise le Spec Gate du snapshot exact affiché dans l'approbation. */
  evaluateSpecGate: (id: number, workspaceId: string) =>
    request<SpecGateEvaluation>(`/approvals/${id}/spec-gate`, {
      method: 'POST',
      body: JSON.stringify({ workspace_id: workspaceId }),
    }),

  /**
   * FND-V05X-004 : rattache le reçu RENOUVELÉ (dernière évaluation PASS) à une spécification
   * approuvée dont la preuve a expiré. L'attestant vient du jeton ; le corps porte le reçu et le
   * bundle exacts examinés, avec l'attestation humaine de la décision.
   */
  renewSpecGateEvidence: (id: number, receiptId: string, sourceBundleSha256: string, comment?: string) =>
    request<SpecGateRenewalResult>(`/approvals/${id}/spec-gate/renewal`, {
      method: 'POST',
      body: JSON.stringify(specGateRenewalBody(receiptId, sourceBundleSha256, comment)),
    }),

  /**
   * Édite une spécification publiée en NOUVELLE version (Spec Editor). Crée une demande
   * d'approbation — décidée par un autre que l'éditeur, puis publiée (SoD). Renvoie le
   * job de l'édition pour la retrouver dans le Centre d'approbations.
   */
  editSpec: (workspaceId: string, artifactPath: string, content: string) =>
    request<{ job_id: string; artifact_path: string; status: string }>(
      '/specs/edit', {
        method: 'POST',
        body: JSON.stringify({
          workspace_id: workspaceId, artifact_path: artifactPath, content,
        }),
      }),

  getSpecGateEvidence: (id: number, workspaceId: string, receiptId: string,
    sourceBundleSha256: string) => request<SpecGateEvidenceDetail>(
      `/approvals/${id}/spec-gate/evidence?${new URLSearchParams({
        workspace_id: workspaceId, receipt_id: receiptId,
        source_bundle_sha256: sourceBundleSha256,
      }).toString()}`),

  /**
   * Édite un artefact de N'IMPORTE quelle famille whitelistée (specs/cadrages/options/
   * personas/processes/backlog/analyses/changes/reviews) — EPIC-COÉCRITURE. L'édition
   * revient en approbation (SoD), comme une génération.
   */
  editArtifact: (workspaceId: string, artifactPath: string, content: string, approvalId?: number) =>
    request<{ job_id: string; artifact_path: string; status: string; version_n?: number }>(
      '/artifacts/edit', {
        method: 'POST',
        body: JSON.stringify({
          workspace_id: workspaceId, artifact_path: artifactPath, content,
          // M6 : la version éditée exactement — l'édition en devient la version suivante de la lignée.
          ...(approvalId != null ? { approval_id: approvalId } : {}),
        }),
      }),

  /** L'identité du décideur vient du jeton, côté serveur : elle ne se transmet pas. */
  decideApproval: (
    id: number,
    decision: 'approved' | 'rejected',
    comment?: string,
    evidence?: SpecGateDecisionEvidence | null,
  ) =>
    request<{
      id: number; decision: string; decided_by: string
      /**
       * ARB-AUD-06 (DC10) : les avertissements qui ACCOMPAGNENT l'approbation sans la bloquer (consignés avec elle) —
       * `[{ code: 'SPEC_BLOCKING_POINTS_UNVERIFIED', points: ['Q-03', …] }]` quand des points bloquants de la
       * spécification sont répondus mais jamais vérifiés. Toujours présents côté passerelle (vides sans avertissement) ;
       * absents d'une passerelle antérieure.
       */
      warnings?: { code: string; points?: string[] }[]
    }>(
      `/approvals/${id}/decision`,
      { method: 'POST', body: JSON.stringify(
        approvalDecisionBody(decision, comment, evidence),
      ) }),

  /**
   * Dépose le VERDICT DE REVUE INDÉPENDANTE qu'exige `approvals_guard_independent_review`
   * (V186) — sans lui, aucun artefact de lignée IA n'est approuvable, et le refus remonte
   * en 500 générique (SQL state P0003 masqué par `GlobalExceptionHandler`).
   *
   * Comme pour `decideApproval`, l'identité du relecteur vient du JETON côté serveur ; et
   * `review_evidence_sha256` est calculée par le serveur à partir de la justification —
   * l'envoyer d'ici serait offrir au client de fabriquer sa propre preuve.
   */
  soumettreRevue: (id: number, verdict: ReviewVerdict, justification: string) =>
    request<{ id: number; review_verdict: string; reviewed_by: string }>(
      `/approvals/${id}/review`,
      { method: 'POST', body: JSON.stringify({ verdict, justification }) }),

  /**
   * O22 (parcours caisse 2026-10-05) — plan de mitigation d'un risque signalé par le relecteur
   * indépendant (`review_verdict = 'risk'`) : propriétaire (membre du projet), mesure, échéance
   * (`AAAA-MM-JJ`, fin de journée UTC). Sans lui, la passerelle refuse l'approbation
   * (`RISK_MITIGATION_PLAN_REQUIRED`) ; l'acteur vient du jeton.
   */
  renseignerMitigationRisque: (id: number, plan: { owner: string; mitigation: string; due_at: string }) =>
    request<{ id: number; risk_owner: string; risk_mitigation: string; risk_mitigation_due_at: string }>(
      `/approvals/${id}/risk-mitigation`,
      { method: 'POST', body: JSON.stringify(plan) }),

  /**
   * Régénère l'artefact d'une approbation avec une CONSIGNE humaine (EPIC-COÉCRITURE) :
   * re-enfile le job d'origine en version suivante. Sans consigne sur un rejet, le
   * serveur réutilise le commentaire de rejet (feedback → prompt).
   */
  regenerateApproval: (id: number, instructions?: string, pointIds?: number[]) =>
    request<{ job_id: string; regenerates_approval: number; version_n: number; status: string; answers_sent?: string[]; answers_characters?: number; previous_instructions_sent?: number[]; previous_instructions_omitted?: number[]; previous_instructions_characters?: number }>(
      `/approvals/${id}/regenerate`,
      // v05x-ux-vague2 (L6) : `point_ids` — réponses jointes en DONNÉES, consigne alors facultative.
      { method: 'POST', body: JSON.stringify({ ...(instructions ? { instructions } : {}), ...(pointIds && pointIds.length > 0 ? { point_ids: pointIds } : {}) }) }),

  /**
   * MJ-07 (FND-V050-148) : les jobs lancés DEPUIS cette approbation (régénération, application,
   * backlog, options…), avec leur état et la cause d'un échec — lus par l'écran qui les a lancés.
   */
  approvalJobs: (id: number) =>
    request<ApprovalLaunchedJob[]>(`/approvals/${id}/jobs`),

  listJobs: (workspaceId: string, query?: string) =>
    request<JobSummary[]>(`/jobs?workspace_id=${encodeURIComponent(workspaceId)}`
      + (query?.trim() ? `&q=${encodeURIComponent(query.trim())}` : '')),
  /** État dérivé d'un job précis (`worker_lost` inclus) — `EtatJob`, glass box. */
  etatJob: (jobId: string) =>
    request<JobSummary>(`/jobs/${encodeURIComponent(jobId)}/etat`),
  /** Action « relancer » d'`EtatJob` : refusée par la passerelle si le job n'est plus
   *  `worker_lost` (409) ou si l'appelant n'a pas le rôle qui agit dans l'espace (403). */
  relancerJob: (jobId: string, workspaceId: string) =>
    request<JobSummary>(
      `/jobs/${encodeURIComponent(jobId)}/relancer?workspace_id=${encodeURIComponent(workspaceId)}`,
      { method: 'POST' }),
  semanticSearchJobs: (workspaceId: string, query: string, limit = 20) =>
    request<SemanticHistoryHit[]>(`/jobs/semantic-search?workspace_id=${encodeURIComponent(workspaceId)}`
      + `&q=${encodeURIComponent(query)}&limit=${limit}`),

  /** Conversation ancrée sur un espace (RAG). L'historique est porté par l'appelant. */
  chat: (workspaceId: string, messages: ChatMessage[], modelId: string) =>
    request<ChatReply>('/chat', {
      method: 'POST',
      body: JSON.stringify({
        workspace_id: workspaceId,
        mode: 'general',
        messages,
        options: { model_id: modelId },
      }),
    }),

  /** Chat server-streaming : chaque delta est livré dès sa réception fournisseur. */
  chatStream: async (
    workspaceId: string,
    messages: ChatMessage[],
    modelId: string,
    onDelta?: (delta: string) => void,
    signal?: AbortSignal,
  ): Promise<ChatReply> => {
    const token = getToken()
    // Une génération locale 27B peut dépasser une minute au cold start, mais un
    // fournisseur bloqué ne doit jamais laisser l'interface en attente indéfiniment.
    // Le composant ChatConsole transforme l'abandon en état d'observabilité dégradée.
    const streamSignal = signal ?? AbortSignal.timeout(180_000)
    const response = await fetch(`${BASE}/chat/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ workspace_id: workspaceId, mode: 'general', messages, options: { model_id: modelId } }),
      signal: streamSignal,
    })
    if (!response.ok || !response.body) throw new ApiError(response.status, httpMessage(response.status))
    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    let content = ''
    let meta: ChatReply['meta'] | undefined
    let usage: ChatReply['usage'] | undefined
    const consume = (frame: string) => {
      const name = frame.split('\n').find((line) => line.startsWith('event:'))?.slice(6).trim()
      const data = frame.split('\n').filter((line) => line.startsWith('data:')).map((line) => line.slice(5).trim()).join('')
      if (!data) return
      if (name === 'done') {
        const final = JSON.parse(data) as NonNullable<ChatReply['meta']> & NonNullable<ChatReply['usage']>
        meta = {
          ...(final.trace_id ? { trace_id: final.trace_id } : {}),
          ...(final.model_id ? { model_id: final.model_id } : {}),
        }
        usage = {
          ...(final.prompt_tokens !== undefined ? { prompt_tokens: final.prompt_tokens } : {}),
          ...(final.completion_tokens !== undefined ? { completion_tokens: final.completion_tokens } : {}),
          ...(final.latency_ms !== undefined ? { latency_ms: final.latency_ms } : {}),
        }
        return
      }
      if (name === 'error') {
        let failure: { code?: string; status?: number; message?: string }
        try {
          failure = JSON.parse(data) as { code?: string; status?: number; message?: string }
        } catch {
          failure = {}
        }
        throw new ApiError(
          typeof failure.status === 'number' ? failure.status : 500,
          failure.message ?? httpMessage(typeof failure.status === 'number' ? failure.status : 500),
          failure.code,
        )
      }
      let delta = data
      try { delta = JSON.parse(data) as string } catch { /* plain SSE data */ }
      content += delta
      onDelta?.(delta)
    }
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      const frames = buffer.split('\n\n')
      buffer = frames.pop() ?? ''
      frames.forEach(consume)
    }
    if (buffer.trim()) consume(buffer)
    return { message: { role: 'assistant', content }, ...(meta ? { meta } : {}), ...(usage ? { usage } : {}) }
  },

  /** Dépendances inter-projets d'un espace, avec leur état de satisfaction (REQ-F-0042). */
  listDependencies: (workspaceId: string) =>
    request<Dependency[]>(
      `/dependencies?workspace_id=${encodeURIComponent(workspaceId)}`),

  addDependency: (workspaceId: string, dependsOn: string, artifactHint: string) =>
    request<{ workspace_id: string; depends_on_workspace_id: string; artifact_hint: string }>(
      '/dependencies', {
        method: 'POST',
        body: JSON.stringify({
          workspace_id: workspaceId,
          depends_on_workspace_id: dependsOn,
          artifact_hint: artifactHint,
        }),
      }),

  removeDependency: (id: number) =>
    request<{ id: number; deleted: boolean }>(`/dependencies/${id}`, { method: 'DELETE' }),

}
